import { makeStateStore } from '@playcastdotio/StateStore';
import type { StateStore } from '@playcastdotio/CoreTypes';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IReplicationRouter } from '../../ReplicationRouter';
import type { AudioSource } from '../stores';

export type AudioManagerState = {
  desktopVolume: number;
  micVolume: number;
  desktopMuted: boolean;
  micMuted: boolean;
  desktopSources: AudioSource[];
  micSources: AudioSource[];
  selectedDesktopDevice: string;
  selectedMicDevice: string;
};

export type AudioManagerDeps = {
  ipcService: IIpcService;
  getHostAuthId: () => string;
};

export class AudioManager {
  private store: StateStore<AudioManagerState>;
  private deps: AudioManagerDeps;

  constructor(deps: AudioManagerDeps) {
    this.deps = deps;
    this.store = makeStateStore<AudioManagerState>({
      desktopVolume: 100,
      micVolume: 100,
      desktopMuted: false,
      micMuted: false,
      desktopSources: [],
      micSources: [],
      selectedDesktopDevice: '',
      selectedMicDevice: '',
    });
  }

  public getStore(): StateStore<AudioManagerState> {
    return this.store;
  }

  public registerReplicationHandler(router: IReplicationRouter): void {
    router.register('audioState', this.onAudioStateReplication.bind(this));
    console.log('[AudioManager] Registered replication handler for audioState');
  }

  public setDesktopVolume(volume: number): void {
    this.store.updateState({ ...this.store.currentState, desktopVolume: volume });
  }

  public setMicVolume(volume: number): void {
    this.store.updateState({ ...this.store.currentState, micVolume: volume });
  }

  public setDesktopMuted(muted: boolean): void {
    this.store.updateState({ ...this.store.currentState, desktopMuted: muted });
  }

  public setMicMuted(muted: boolean): void {
    this.store.updateState({ ...this.store.currentState, micMuted: muted });
  }

  public selectDesktopSource(deviceId: string): void {
    this.store.updateState({ ...this.store.currentState, selectedDesktopDevice: deviceId });
  }

  public selectMicSource(deviceId: string): void {
    this.store.updateState({ ...this.store.currentState, selectedMicDevice: deviceId });
  }

  public updateDeviceLists(desktopSources: AudioSource[], micSources: AudioSource[]): void {
    const selectedDesktop = desktopSources.find((s) => s.selected)?.deviceId ?? '';
    const selectedMic = micSources.find((s) => s.selected)?.deviceId ?? '';
    this.store.updateState({
      ...this.store.currentState,
      desktopSources,
      micSources,
      selectedDesktopDevice: selectedDesktop,
      selectedMicDevice: selectedMic,
    });
  }

  private onAudioStateReplication(state: unknown): void {
    if (!state || typeof state !== 'object') return;
    const audio = state as Record<string, unknown>;

    const updates: Partial<AudioManagerState> = {};
    if (typeof audio.desktopAudioVolume === 'number') updates.desktopVolume = audio.desktopAudioVolume;
    if (typeof audio.microphoneVolume === 'number') updates.micVolume = audio.microphoneVolume;
    if (typeof audio.desktopAudioMuted === 'boolean') updates.desktopMuted = audio.desktopAudioMuted;
    if (typeof audio.microphoneMuted === 'boolean') updates.micMuted = audio.microphoneMuted;

    if (Array.isArray(audio.desktopAudioSources)) {
      updates.desktopSources = audio.desktopAudioSources as AudioSource[];
      updates.selectedDesktopDevice = (audio.desktopAudioSources as AudioSource[]).find((s) => s.selected)?.deviceId ?? this.store.currentState.selectedDesktopDevice;
    }
    if (Array.isArray(audio.microphoneSources)) {
      updates.micSources = audio.microphoneSources as AudioSource[];
      updates.selectedMicDevice = (audio.microphoneSources as AudioSource[]).find((s) => s.selected)?.deviceId ?? this.store.currentState.selectedMicDevice;
    }

    if (Object.keys(updates).length > 0) {
      this.store.updateState({ ...this.store.currentState, ...updates });
    }
  }
}
